import Table, { SelectColumnNoSelectAll } from "components/Table/Table";
import { SECTION } from "constants/SettingsConstants";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useEffect, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { SELECT_COLUMN_KEY } from "react-data-grid";
import { useNavigate } from "react-router-dom";
import { setSelectedIds } from "reducers/future-medical";
import { setIsLoading } from "reducers/main";
import { useGetMSAsQuery } from "services/msa-api";
import type { MSADataType } from "types/DataTypes";
import { useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

export type MSATableDataType = {
  file_id: string;
  id: string;
  client_name: string;
  created: string;
  claimant_name: string;
  claim_numbers: string;
  workspace_url: string;
};

type MSATableProps = {
  serviceType?: "MSA" | "ARV";
};

const MSATable = ({ serviceType = "MSA" }: MSATableProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const query = useQuery();
  const [page, setPage] = useState(1);

  const { data = { results: [], count: 0 }, isLoading } = useGetMSAsQuery({
    type: serviceType,
    searchTerm: query.get(GLOBAL_SEARCH_PARAM),
    limit: PAGE_SIZE,
    offset: OFFSET(page),
  });

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [isLoading, dispatch]);

  const handleCellClick = (args: CellClickArgs<MSATableDataType>) => {
    // the checkbox column selects the row; it should not open the workspace
    if (args.column.key === SELECT_COLUMN_KEY) return;
    const workspaceUrl = args.row.workspace_url;
    navigate(workspaceUrl);
  };

  const handleSelectedRowsChange = (newSelectedRows: Set<string | number>) => {
    if (newSelectedRows.size > 1) {
      const lastSelected = Array.from(newSelectedRows).pop()!;
      setSelectedIds([lastSelected]);
      dispatch(setSelectedIds([lastSelected]));
    } else {
      setSelectedIds(Array.from(newSelectedRows));
      dispatch(setSelectedIds(Array.from(newSelectedRows)));
    }
  };

  const rows: MSATableDataType[] = data.results.map((msa: MSADataType) => ({
    file_id: msa.file.id.toString(),
    id: msa.id.toString(),
    client_name: msa.client.name,
    created: msa.created,
    claimant_name: msa.claimant.first_name,
    claim_numbers: msa.file.claim_numbers
      .map((claim_number) => claim_number.number)
      .join(", "),
    workspace_url: msa.workspace_url,
  }));

  const SERVICE_TYPE_LABELS = {
    MSA: SECTION.MSA,
    ARV: SECTION.AMENDED_REVIEW,
  };

  const MSA_COLUMNS = [
    SelectColumnNoSelectAll,
    { key: "id", name: "ID", ...commonColumnProps },
    { key: "client_name", name: "Client Name", ...commonColumnProps },
    { key: "created", name: "Created", ...commonColumnProps },
    { key: "claimant_name", name: "Claimant Name", ...commonColumnProps },
    { key: "claim_numbers", name: "Claim Numbers", ...commonColumnProps },
    {
      key: "type",
      name: "Type",
      ...commonColumnProps,
      formatter: ({ row }) => SERVICE_TYPE_LABELS[row.type] || row.type,
    },
  ];

  return (
    <Table
      columns={MSA_COLUMNS}
      rows={rows}
      rowKeyGetter={(row: MSATableDataType) => row.id}
      onCellClick={handleCellClick}
      onSelectedRowsChange={handleSelectedRowsChange}
      fullHeight
      pagination={{
        pageSize: PAGE_SIZE,
        pageIndex: page,
        rowCount: data.count,
        onPageChange: setPage,
        isLoading,
      }}
    />
  );
};

export default MSATable;
